"use client";

import React, { useEffect, useState } from "react";
import { Card, Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import Chart from "react-apexcharts";
import dayjs from 'dayjs';

const OrderSummary = ({ recentorders }) => {
  const [isChartLoaded, setChartLoaded] = useState(false);
  const [series, setSeries] = useState([0, 0]); // For Completed and Pending counts
  const [percentages, setPercentages] = useState({ completed: 0, pending: 0 });
  const [selectedPeriod, setSelectedPeriod] = useState("all"); // Default to 'all'

  const filterOrdersByPeriod = (orders, period) => {
    if (!Array.isArray(orders)) return [];

    const now = dayjs();
    let startDate = null;

    switch (period) {
      case "today":
        startDate = now.startOf('day'); 
        break;
      case "weekly":
        startDate = now.subtract(7, 'day').startOf('day');
        break;
      case "monthly":
        startDate = now.subtract(1, 'month').startOf('day');
        break;
      case "yearly":
        startDate = now.subtract(1, 'year').startOf('day');
        break;
      case "all":
      default:
        return orders; 
    }

    return orders.filter(order => {
      const orderDate = dayjs(order.created_at);
      return orderDate.isAfter(startDate) || orderDate.isSame(startDate, 'day');
    });
  };

  useEffect(() => {
    if (!Array.isArray(recentorders)) {
      setSeries([0, 0]);
      setPercentages({ completed: 0, pending: 0 });
      setChartLoaded(true);
      return;
    }

    const filteredOrders = filterOrdersByPeriod(recentorders, selectedPeriod);
    const quotationStatuses = filteredOrders.flatMap((order) =>
      order.dealer_quotations.map((quotation) => quotation.status.toLowerCase())
    );

    const completedCount = quotationStatuses.filter(
      (status) => status === "finalized" || status === "completed"
    ).length;
    const pendingCount = quotationStatuses.filter(
      (status) => status === "pending"
    ).length;
    const total = completedCount + pendingCount;

    const completedPercentage = total > 0 ? Math.round((completedCount / total) * 100) : 0;
    const pendingPercentage = total > 0 ? Math.round((pendingCount / total) * 100) : 0;

    setSeries([completedCount, pendingCount]);
    setPercentages({ completed: completedPercentage, pending: pendingPercentage });
    setChartLoaded(true);
  }, [recentorders, selectedPeriod]);

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  const options = {
    labels: ["Completed", "Pending"],
    colors: ["#37D80A", "#AD63F6"],
    legend: {
      show: true,
      position: "top",
      fontSize: "12px",
      horizontalAlign: "center",
      itemMargin: {
        horizontal: 8,
        vertical: 0,
      },
      labels: {
        colors: "#64748B",
      },
      markers: {
        offsetX: -2,
        offsetY: -0.5,
        shape: "diamond",
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "25px",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "16px", md: "18px" },
            fontWeight: 700,
          }}
          className="text-black"
        >
          Enquiry Summary
        </Typography>

        <Box>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Select time period' }}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mb: "-8px" }}>
        {isChartLoaded && (
          <Chart
            options={options}
            series={series}
            type="donut"
            height={300}
            width={"100%"}
          />
        )}
      </Box>

      <Box>
        <Box sx={{ mt: "6px" }}>
          <Typography component="span" sx={{ fontWeight: "500" }}>
            Completed Order
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "15px",
            }}
          >
            <Box
              sx={{
                bgcolor: "#ecf0ff",
                width: "100%",
                height: "4px",
                display: "block",
                borderRadius: "30px",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#25B003",
                  width: `${percentages.completed}%`,
                  height: "4px",
                  borderRadius: "30px",
                }}
              ></Box>
            </Box>
            <Typography component="span">{percentages.completed}%</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: "6px" }}>
          <Typography component="span" sx={{ fontWeight: "500" }}>
            Pending Order
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "15px",
            }}
          >
            <Box
              sx={{
                bgcolor: "#ecf0ff",
                width: "100%",
                height: "4px",
                display: "block",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#AD63F6",
                  width: `${percentages.pending}%`,
                  height: "4px",
                }}
              ></Box>
            </Box>
            <Typography component="span">{percentages.pending}%</Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default OrderSummary;